<template>
  <figure
    class="OptionList"
    :class="{
      'OptionList--Swatches': option.name === 'Color',
      'OptionList--Hidden': hideOptions,
    }"
    :data-options-shown="shownOptionValues"
  >
    <figcaption
      :class="[{ 'Label--Colors': label == 'Color' }, 'OptionList__Label']"
    >
      <div class="OptionList__Heading">
        <span class="p4">{{ label }}</span>
        <span v-if="tooltip(label)" class="p4 SwatchLabel">
          <Tooltip :text="tooltip(label)" />
        </span>
        <span v-if="getSelectedValue !== ''" class="t4">{{
          getSelectedValue
        }}</span>
      </div>
      <span
        v-if="(showTooltip || autoBundleLabel) && tooltipLabel"
        class="p4 SwatchLabel"
      >
        <span class="p4">{{ tooltipLabel }}</span>

        <Tooltip :text="showTooltip" />
      </span>
      <span
        v-if="option.name.includes(modalKey) && isPdp"
        class="p5"
        data-type="modal"
        :for="`${modalKey.toLowerCase()}-${data.item.handle}`"
        >{{
          store?.terms?.modal[modalKey.toLowerCase()] || modalKey.toLowerCase()
        }}</span
      >

      <p v-if="messaging" class="OptionList__Messaging p5">{{ messaging }}</p>
    </figcaption>
    <ul class="OptionList__Items" :data-name="option.name">
      <OptionSelector
        v-for="value in option.values"
        @handle-selection="handleSelection"
        :is-auto-bundle="isAutoBundle"
        :is-gift-card="isGiftCard"
        :active="getSelectedValue"
        :option="option.name"
        :data="data"
        :index="index"
        :value="value"
        :key="value"
        :location="location"
      />
    </ul>
  </figure>
</template>

<script setup>
import Tooltip from '../Global/Tooltip.vue';
import addOverlayListeners from '@utils/overlay';
import OptionSelector from './OptionSelector.vue';
import { useProductStore } from './Composables/Store';
import { fetchStorefront } from '@utils/GraphQLClient';
import { ref, onMounted, onUpdated, computed } from 'vue';
import { OPTION_MESSAGING_QUERY } from './Composables/Queries';
import { formatValue, handleUniqueOptionValues } from '@utils/ProductResources';

const emits = defineEmits(['update-product']);
const props = defineProps({
  option: { type: Object, required: true },
  data: { type: Object, required: false, default: false },
  edit: { type: Boolean, required: false, default: false },
  isAutoBundle: { type: Boolean, required: false, default: false },
  isGiftCard: { type: Boolean, required: false, default: false },
  location: { type: String, required: false },
});

const index = ref(0);
const swatchLabel = ref('');
const messaging = ref(false);
const optionCount = ref(false);
const store = useProductStore();
const label = computed(() => {
  const useAlias =
    props?.option.alias == 'Final Sale' || props.option?.secondary;

  return useAlias ? props?.option.alias : props?.option.name;
});
const modalKey = computed(() =>
  props?.data?.modals?.find((key) => props?.option.name.includes(key))
);
const isQV = computed(() => props.location == 'quick-view');
const isPdp = window.location.pathname.includes('/products') && !isQV.value;

const autoBundleLabel = computed(
  () => props.isAutoBundle && props.option.name === 'Color' && swatchLabel.value
);
const getSelectedValue = computed(() => {
  const isStepped = store.isSteppedBundle;
  const useLocalData = props.edit || isStepped;

  const options = useLocalData
    ? props.data.selected.options
    : store.$state.selected.options;
  const selection = options[index.value];
  if (!selection || selection === '') return selection;
  const match = props.option.values.filter(
    (option) => option === selection.value
  );
  const formattedMatch = formatValue(match[0]) || match[0];

  return match.length > 0 ? formattedMatch : '';
});

const optionSum = computed(() =>
  Object.values(props.option.count).reduce((a, b) => a + b, 0)
);

const evergreenSwatchCount = computed(() =>
  isQV.value
    ? props.data.evergreenSwatchCount
    : store.$state.evergreenSwatchCount
);

const showTooltip = computed(() => {
  if (props.option.count) {
    const countLength = optionSum.value !== props.option.values.length;
    optionCount.value =
      countLength && store.$state.edit.secondaryItemsOptions
        ? store.$state.edit.secondaryItemsOptions
        : props.option.count;
    return tooltip(tooltipLabel.value);
  } else if (evergreenSwatchCount.value) return tooltip(swatchLabel.value.name);
  else return tooltip(props.option);
});

const tooltipLabel = computed(() => {
  const selectedIndex = props.option.values.findIndex(
    (value) => value == getSelectedValue.value
  );
  const length = props.option.values.length;
  if (optionCount.value && props.option.count) {
    let count = 0;
    let selectedAlias;
    for (const prop in optionCount.value) {
      count += optionCount.value[prop];
      if (selectedIndex <= count - 1) {
        selectedAlias = prop;
        break;
      }
    }
    return selectedAlias;
  } else if (evergreenSwatchCount) return swatchLabel.value.name;
  else return false;
});

const tooltip = (option) => {
  if (!option) return false;

  let name =
    typeof option == 'string'
      ? option
      : option?.alias
      ? option.alias
      : option.name;
  const key = name.replace(/\s+/g, '_').toLowerCase();
  const hasTooltip = Object.keys(store.$state.terms.tooltips).includes(key);
  if (!hasTooltip) return false;

  return store.$state.terms.tooltips[key];
};

const hideOptions = computed(() => {
  const option = props.option;
  return option.values.includes('Default Title');
});

const findOptionIndex = () => {
  let options = isQV.value
    ? props.data.item.options
    : store.$state.item.options;
  const isSteppedBundle = store.isSteppedBundle && !isQV.value;
  if (isSteppedBundle) {
    const current = store.getCurrentStep;
    const item = current?.item;
    options = item?.options;
  }

  if (props.edit) {
    options = props.data.item.options;
  }

  return options.findIndex((option) => option.name === props?.option.name);
};

const handleSelection = (value) => {
  const { name } = props.option;
  const payload = { value, name };
  const isStepped = store.isSteppedBundle && !isQV.value;

  if (props.data?.item?.handle == 'mib-pillows') {
    if (value == 'Down Alt') {
      let newValue = 'Down Alternative';
      value = newValue;
      payload.value = newValue;
    }
    handleUniqueOptionValues({ value, props, index, store });
  }

  if (props.edit) {
    return emits('update-product', { index: index.value, option: payload });
  }

  if (isStepped) {
    store.componentSelect(index.value, payload);
  } else {
    store.select(index.value, payload);
  }
};

const handleSwatchLabel = (selectorIndex) => {
  // increment the optionIndex bc the number in the tag is 1-indexed
  if (!evergreenSwatchCount.value) return;
  selectorIndex++;
  swatchLabel.value =
    selectorIndex > evergreenSwatchCount.value
      ? store.$state.terms.option_labels.seasonal
      : false;
};

const shownOptionValues = computed(() => {
  const data = {
    value: props?.data?.selected?.options[0]?.value,
    props,
    index,
    store,
  };
  return index.value == 2
    ? handleUniqueOptionValues(data)
    : props?.option?.value?.length;
});

const fetchMessaging = async () => {
  const query = OPTION_MESSAGING_QUERY;
  const option = label?.value ?? false;
  const handle = props?.data?.item?.handle ?? false;
  if (!handle || !option) return;

  const request = { variables: { handle }, query };
  const response = await fetchStorefront(request).then((res) => {
    const fields = res?.data?.product ?? false;
    if (!fields) return false;

    const location = fields?.location?.value ?? false;
    const messaging = fields?.messaging?.value ?? false;
    if (!location || !messaging) return false;

    const options = JSON.parse(location) ?? [];
    const index = options.findIndex((name) => name === option);
    if (index <= -1) return false;

    const messages = JSON.parse(messaging) ?? [];
    const message = messages[index] ?? false;
    return message;
  });
  return response;
};

const checkQVSwatch = () => {
  if (props.option.name == 'Color' && isQV.value) {
    handleSwatchLabel(props.option.values.indexOf(getSelectedValue.value));
  }
};

onMounted(async () => {
  index.value = findOptionIndex();
  addOverlayListeners();
  if (index.value == 0 && props.data?.item?.handle == 'mib-pillows')
    handleSelection(getSelectedValue.value);
  messaging.value = await fetchMessaging();
  checkQVSwatch();
});

onUpdated(() => {
  addOverlayListeners();
  if (!props.option?.secondary) {
    if (isQV.value) return;
    else handleSwatchLabel(props.option.values.indexOf(getSelectedValue.value));
  }
});
</script>
